import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Loader2, Upload, X, User, Mountain, Package,
  Image as ImageIcon, FolderOpen, ChevronRight, Home, Pencil, Globe, Lock, Copy,
  Sparkles, Scissors, Check, Search,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import LibraryGenerator from '../components/LibraryGenerator';
import {
  listAllLibraryItems, createLibraryItem, deleteLibraryItem, renameLibraryItem, moveLibraryItem,
  listFolders, createFolder, renameFolder, deleteFolder, moveFolder,
  importLibraryItems, setFolderPublic, duplicateFolder,
  refineLibraryItem, removeLibraryBackground,
} from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_REFS = 7;
const DND_MIME = 'application/x-kinote-lib';

const KIND_META = {
  characters: { label: 'Personnages', singular: 'personnage', icon: User,     placeholder: 'Ex : Sarah, l\'inspectrice',           badge: 'bg-blue-500/10 text-blue-600' },
  decors:     { label: 'Décors',       singular: 'décor',       icon: Mountain, placeholder: 'Ex : Bar des Lilas, intérieur jour',    badge: 'bg-green-500/10 text-green-600' },
  objects:    { label: 'Objets',       singular: 'objet',       icon: Package,  placeholder: 'Ex : Pistolet de service, mallette',    badge: 'bg-orange-500/10 text-orange-600' },
};

const itemSrc = (item) =>
  item?.canonical_url
    ? (item.canonical_url.startsWith('http') ? item.canonical_url : `${API_URL}${item.canonical_url}`)
    : null;

function buildBreadcrumb(folders, currentFolderId) {
  const crumbs = [];
  let id = currentFolderId;
  while (id) {
    const f = folders.find((x) => x.id === id);
    if (!f) break;
    crumbs.unshift(f);
    id = f.parent_id;
  }
  return crumbs;
}

// True if `descendantId` is found under `ancestorId` (any depth).
// Used to forbid nesting a folder into one of its own descendants.
function isDescendantOf(descendantId, ancestorId, folders) {
  let id = descendantId;
  const seen = new Set();
  while (id && !seen.has(id)) {
    seen.add(id);
    const f = folders.find((x) => x.id === id);
    if (!f) return false;
    if (f.parent_id === ancestorId) return true;
    id = f.parent_id;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Inline-rename input
// ---------------------------------------------------------------------------
function InlineEdit({ value, onSave, onCancel, autoFocus = true }) {
  const [draft, setDraft] = useState(value);
  const ref = useRef(null);
  useEffect(() => { if (autoFocus) { ref.current?.focus(); ref.current?.select(); } }, [autoFocus]);
  const commit = () => {
    const t = draft.trim();
    if (t && t !== value) onSave(t);
    else onCancel();
  };
  return (
    <input
      ref={ref}
      className="w-full text-sm border border-primary rounded px-1.5 py-0.5 bg-background focus:outline-none"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// ---------------------------------------------------------------------------
// Folder card — Frame.io-inspired: tab on top + 2x2 thumbnail preview
// ---------------------------------------------------------------------------
function FolderCard({ folder, thumbnails, itemCount, onOpen, onRename, onDelete, onTogglePublic, onDuplicate, dragSourceId, isDropTarget, isAdmin, ...dnd }) {
  const [editing, setEditing] = useState(false);
  const isBeingDragged = dragSourceId === folder.id;
  const countLabel = itemCount === 0 ? 'Vide' : itemCount === 1 ? '1 élément' : `${itemCount} éléments`;
  // Non-admin viewers can never own a public folder, but the admin always can.
  // `is_owner` comes from the backend serializer.
  const ownedByMe = folder.is_owner !== false;
  const isPublic = !!folder.is_public;
  const dragProps = ownedByMe ? { draggable: true, ...dnd } : {};

  return (
    <div
      className={`group relative cursor-pointer transition-all ${isBeingDragged ? 'opacity-30 scale-95' : ''}`}
      onClick={() => !editing && onOpen(folder.id)}
      {...dragProps}
    >
      <div
        className={`relative bg-card border rounded-xl overflow-hidden transition-all ${
          isDropTarget
            ? 'border-primary ring-2 ring-primary/40 shadow-lg shadow-primary/10'
            : isPublic
              ? 'border-primary/40 group-hover:border-primary/60 group-hover:shadow-md'
              : 'border-border group-hover:border-primary/40 group-hover:shadow-md'
        }`}
      >
        {/* 2x2 thumbnail grid with padding for breathing room */}
        <div className="p-2 relative">
          <div className="grid grid-cols-2 gap-1.5 aspect-video rounded-lg overflow-hidden bg-secondary/20">
            {Array.from({ length: 4 }, (_, i) => {
              const t = thumbnails[i];
              const src = itemSrc(t);
              return (
                <div key={i} className="bg-secondary/30 overflow-hidden">
                  {src && <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" draggable={false} />}
                </div>
              );
            })}
          </div>
          {isPublic && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/90 text-primary-foreground">
              <Globe className="w-2.5 h-2.5" /> Public
            </span>
          )}
        </div>
        {/* Name + count + actions */}
        <div className="px-3 pb-2.5 flex items-center gap-1.5">
          {editing ? (
            <InlineEdit
              value={folder.name}
              onSave={(n) => { onRename(folder.id, n); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <div className="flex-1 min-w-0">
              <span
                className="text-sm font-medium truncate block"
                onDoubleClick={(e) => { ownedByMe && (e.stopPropagation(), setEditing(true)); }}
                title={folder.name}
              >
                {folder.name}
              </span>
              <span className="text-[11px] text-muted-foreground">{countLabel}</span>
            </div>
          )}
          {!editing && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              {/* Duplicate visible for all users (own + public) */}
              <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); onDuplicate(folder); }} title="Dupliquer">
                <Copy className="w-3 h-3" />
              </button>
              {ownedByMe && isAdmin && (
                <button type="button"
                  className={`p-1 transition-colors ${isPublic ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                  onClick={(e) => { e.stopPropagation(); onTogglePublic(folder); }}
                  title={isPublic ? 'Rendre privé' : 'Rendre public'}>
                  {isPublic ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                </button>
              )}
              {ownedByMe && (
                <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); setEditing(true); }} title="Renommer">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
              {ownedByMe && (
                <button type="button" className="p-1 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(folder); }} title="Supprimer">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item card
// ---------------------------------------------------------------------------
function ItemCard({ item, onDelete, onPreview, onRename, onSelect, selected, deleting, dragSourceId, ...dnd }) {
  const [editing, setEditing] = useState(false);
  const src = itemSrc(item);
  const isBeingDragged = dragSourceId === item.id;
  // Items inside a public admin folder come back with is_owner=false. They
  // can still be dragged onto the editor canvas (read-only use), but cannot
  // be renamed, moved, or deleted by the viewer.
  const ownedByMe = item.is_owner !== false;
  const dragProps = ownedByMe ? { draggable: true, ...dnd } : { draggable: true, onDragStart: dnd.onDragStart, onDragEnd: dnd.onDragEnd };

  return (
    <div
      className={`group bg-card border rounded-lg overflow-hidden transition-all ${
        isBeingDragged ? 'opacity-30 scale-95'
        : selected ? 'border-primary ring-2 ring-primary/30 shadow-md shadow-primary/10'
        : 'border-border hover:border-primary/40 hover:shadow-md'
      }`}
      {...dragProps}
    >
      <button
        type="button"
        onClick={(e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); onSelect?.(item); } else onPreview(item); }}
        className="block w-full aspect-video bg-secondary/30 relative overflow-hidden cursor-zoom-in"
      >
        {selected && (
          <div className="absolute top-1.5 left-1.5 z-10 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm pointer-events-none">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        )}
        {src
          ? <img src={src} alt={item.name} className="w-full h-full object-cover" loading="lazy" draggable={false} />
          : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-muted-foreground/20" /></div>}
      </button>
      <div className="px-3 py-2 flex items-center gap-1.5">
        {editing ? (
          <InlineEdit
            value={item.name}
            onSave={(n) => { onRename(item, n); setEditing(false); }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <span
            className="flex-1 text-sm font-medium truncate"
            onDoubleClick={() => ownedByMe && setEditing(true)}
            title={ownedByMe ? "Double-cliquez pour renommer" : item.name}
          >
            {item.name}
          </span>
        )}
        {!editing && ownedByMe && (
          <button
            className="text-muted-foreground opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:text-destructive transition-all p-1 -m-1 disabled:opacity-30 shrink-0"
            onClick={() => onDelete(item)}
            disabled={deleting}
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Creation modal
// ---------------------------------------------------------------------------
function CreateModal({ kind, folderId, onCreated, onClose }) {
  const meta = KIND_META[kind];
  const [name, setName] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    const remaining = MAX_REFS - files.length;
    const accepted = arr.slice(0, Math.max(0, remaining));
    if (accepted.length < arr.length) toast.error(`Maximum ${MAX_REFS} images`);
    setFiles((p) => [...p, ...accepted]);
    setPreviews((p) => [...p, ...accepted.map((f) => URL.createObjectURL(f))]);
  };
  const removeAt = (i) => {
    setFiles((p) => p.filter((_, j) => j !== i));
    setPreviews((p) => { URL.revokeObjectURL(p[i]); return p.filter((_, j) => j !== i); });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Le nom est requis'); return; }
    setSubmitting(true);
    const tid = toast.loading('Synthèse en cours… (30-60s)');
    try {
      const created = await createLibraryItem(kind, trimmed, files, folderId, customPrompt);
      toast.dismiss(tid);
      toast.success(`${meta.singular.charAt(0).toUpperCase() + meta.singular.slice(1)} créé`);
      previews.forEach(URL.revokeObjectURL);
      onCreated(created);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err?.response?.data?.detail || 'Échec de la création');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <meta.icon className="w-4 h-4" /> Nouveau {meta.singular}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Nom <span className="text-destructive">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={meta.placeholder} maxLength={120} disabled={submitting} autoFocus />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Images de référence ({files.length}/{MAX_REFS})</Label>
            <div className="grid grid-cols-4 gap-2">
              {previews.map((src, i) => (
                <div key={src} className="relative aspect-square rounded-md border overflow-hidden bg-secondary/20 group">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button type="button" disabled={submitting}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100"
                    onClick={() => removeAt(i)}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {files.length < MAX_REFS && (
                <button type="button" disabled={submitting}
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square flex flex-col items-center justify-center gap-1 rounded-md border border-dashed hover:border-primary/50 hover:bg-primary/5 transition-all text-muted-foreground disabled:opacity-50">
                  <Upload className="w-4 h-4" /><span className="text-[10px]">Ajouter</span>
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => { addFiles(e.target.files); fileInputRef.current.value = ''; }} />
            <p className="text-[11px] text-muted-foreground/70 mt-2">Jusqu'à {MAX_REFS} images (optionnel), converties en noir et blanc.</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Prompt personnalisé <span className="opacity-50">(optionnel)</span></Label>
            <Textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Précisez la scène ou l'action…"
              rows={2}
              maxLength={500}
              disabled={submitting}
              className="text-xs resize-none"
            />
            <p className="text-[11px] text-muted-foreground/70 mt-1">Décrivez la scène ou l'action pour guider la génération.</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={submitting}>Annuler</Button>
            <Button type="submit" size="sm" disabled={submitting || !name.trim()} className="gap-1.5">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {submitting ? 'Génération…' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// New folder dialog
// ---------------------------------------------------------------------------
function NewFolderDialog({ onConfirm, onClose }) {
  const [name, setName] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader><DialogTitle>Nouveau dossier</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); const t = name.trim(); if (t) onConfirm(t); }} className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du dossier" autoFocus maxLength={80} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
            <Button type="submit" size="sm" disabled={!name.trim()}>Créer</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Move-to-folder picker
// ---------------------------------------------------------------------------
function MoveToFolderDialog({ folders, currentFolderId, onMove, onClose }) {
  const owned = folders.filter((f) => f.id !== currentFolderId && f.is_owner !== false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader><DialogTitle>Déplacer vers…</DialogTitle></DialogHeader>
        <div className="space-y-0.5 max-h-72 overflow-y-auto py-1">
          {currentFolderId !== null && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-secondary/50 flex items-center gap-2 transition-colors"
              onClick={() => onMove(null)}
            >
              <Home className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span>Bibliothèque <span className="text-muted-foreground text-xs">(racine)</span></span>
            </button>
          )}
          {owned.map((f) => (
            <button
              key={f.id}
              type="button"
              className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-secondary/50 flex items-center gap-2 transition-colors"
              onClick={() => onMove(f.id)}
            >
              <FolderOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{f.name}</span>
            </button>
          ))}
          {currentFolderId === null && owned.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">Aucun dossier disponible.</p>
          )}
        </div>
        <div className="flex justify-end pt-2 border-t border-border">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Library() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [creatingKind, setCreatingKind] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);

  // Drag-and-drop state. `dragData` describes what's being dragged;
  // `dropTargetId` highlights the active drop zone (folder id or 'root').
  const [dragData, setDragData] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/', { replace: true });
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, it] = await Promise.all([listFolders(), listAllLibraryItems()]);
      setFolders(Array.isArray(f) ? f : []);
      setItems(Array.isArray(it) ? it : []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  // Derived
  const subfolders = useMemo(
    () => folders.filter((f) => (f.parent_id || null) === currentFolderId),
    [folders, currentFolderId],
  );
  const currentItems = useMemo(
    () => items.filter((it) => (it.folder_id || null) === currentFolderId),
    [items, currentFolderId],
  );
  const breadcrumb = buildBreadcrumb(folders, currentFolderId);

  const availableCategories = useMemo(() => {
    const cats = [...new Set(currentItems.map(it => it.category).filter(Boolean))].sort();
    return cats;
  }, [currentItems]);

  const filteredItems = useMemo(() => {
    let result = currentItems;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(it => it.name.toLowerCase().includes(q));
    }
    if (categoryFilter) {
      result = result.filter(it => it.category === categoryFilter);
    }
    return result;
  }, [currentItems, searchQuery, categoryFilter]);

  // Direct items in a folder — thumbnails capped to 4, count is the full number.
  const folderThumbnails = useCallback(
    (folderId) => items.filter((it) => (it.folder_id || null) === folderId).slice(0, 4),
    [items],
  );
  const folderItemCount = useCallback(
    (folderId) => items.filter((it) => (it.folder_id || null) === folderId).length,
    [items],
  );

  // ---- Folder ops ----
  const handleCreateFolder = async (name) => {
    try {
      const f = await createFolder(name, currentFolderId);
      setFolders((p) => [...p, f]);
      setShowNewFolder(false);
    } catch { toast.error('Échec de la création du dossier'); }
  };
  const handleRenameFolder = async (id, name) => {
    try {
      await renameFolder(id, name);
      setFolders((p) => p.map((f) => f.id === id ? { ...f, name } : f));
    } catch { toast.error('Échec du renommage'); }
  };
  const handleDuplicateFolder = async (folder) => {
    const tid = toast.loading('Duplication en cours…');
    try {
      await duplicateFolder(folder.id);
      // Reload folders AND items — the duplicated folder's items are in the DB
      // but not in local state, so a full reload is the simplest correct fix.
      await load();
      toast.dismiss(tid);
      toast.success(`Dossier « ${folder.name} (copie) » dupliqué`);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err?.response?.data?.detail || `Échec de la duplication (${err?.response?.status ?? 'réseau'})`);
    }
  };
  const handleTogglePublic = async (folder) => {
    const next = !folder.is_public;
    setFolders((p) => p.map((f) => f.id === folder.id ? { ...f, is_public: next } : f));
    try {
      await setFolderPublic(folder.id, next);
      toast.success(next ? 'Dossier rendu public' : 'Dossier remis en privé');
    } catch {
      setFolders((p) => p.map((f) => f.id === folder.id ? { ...f, is_public: !next } : f));
      toast.error('Échec du changement');
    }
  };
  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Supprimer « ${folder.name} » ? Le contenu sera déplacé vers le dossier parent.`)) return;
    try {
      await deleteFolder(folder.id);
      const parentId = folder.parent_id || null;
      setFolders((p) => p.filter((f) => f.id !== folder.id).map((f) => f.parent_id === folder.id ? { ...f, parent_id: parentId } : f));
      setItems((p) => p.map((it) => it.folder_id === folder.id ? { ...it, folder_id: parentId } : it));
      if (currentFolderId === folder.id) setCurrentFolderId(parentId);
      toast.success('Dossier supprimé');
    } catch { toast.error('Échec de la suppression'); }
  };

  // ---- Direct import (no AI synthesis, just B&W conversion) ----
  const handleImportFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setImporting(true);
    const tid = toast.loading(`Import de ${files.length} image${files.length > 1 ? 's' : ''}…`);
    try {
      const created = await importLibraryItems(files, { folderId: currentFolderId });
      setItems((p) => [...created, ...p]);
      toast.dismiss(tid);
      toast.success(`${created.length} image${created.length > 1 ? 's importées' : ' importée'}`);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err?.response?.data?.detail || "Échec de l'import");
    } finally {
      setImporting(false);
    }
  };

  // ---- Item ops ----
  const handleCreated = (item) => { setItems((p) => [item, ...p]); setCreatingKind(null); };
  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer « ${item.name} » ?`)) return;
    setDeletingId(item.id);
    try {
      await deleteLibraryItem(item.kind, item.id);
      setItems((p) => p.filter((x) => x.id !== item.id));
      toast.success('Supprimé');
    } catch { toast.error('Échec de la suppression'); }
    finally { setDeletingId(null); }
  };
  const handleRenameItem = async (item, name) => {
    try {
      await renameLibraryItem(item.kind, item.id, name);
      setItems((p) => p.map((x) => x.id === item.id ? { ...x, name } : x));
    } catch { toast.error('Échec du renommage'); }
  };

  // ---- Multi-selection ----
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const toggleSelect = useCallback((item) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    const count = selectedIds.size;
    if (!count) return;
    const label = count === 1 ? '1 élément sélectionné' : `${count} éléments sélectionnés`;
    if (!window.confirm(`Supprimer ${label} ? Cette action est irréversible.`)) return;
    const toDelete = currentItems.filter((it) => selectedIds.has(it.id));
    clearSelection();
    const tid = toast.loading('Suppression…');
    let errors = 0;
    await Promise.all(toDelete.map(async (item) => {
      try {
        await deleteLibraryItem(item.kind, item.id);
        setItems((p) => p.filter((x) => x.id !== item.id));
      } catch { errors++; }
    }));
    toast.dismiss(tid);
    if (errors) toast.error(`${errors} suppression(s) échouée(s)`);
    else toast.success(`${count} élément${count > 1 ? 's supprimés' : ' supprimé'}`);
  }, [selectedIds, currentItems, clearSelection]); // eslint-disable-line

  const handleBulkMove = useCallback(async (targetFolderId) => {
    const toMove = currentItems.filter((it) => selectedIds.has(it.id));
    if (!toMove.length) return;
    clearSelection();
    setShowMoveDialog(false);
    const tid = toast.loading('Déplacement…');
    let errors = 0;
    await Promise.all(toMove.map(async (item) => {
      try {
        await moveLibraryItem(item.kind, item.id, targetFolderId);
        setItems((p) => p.map((x) => x.id === item.id ? { ...x, folder_id: targetFolderId } : x));
      } catch { errors++; }
    }));
    toast.dismiss(tid);
    if (errors) toast.error(`${errors} déplacement(s) échoué(s)`);
    else toast.success('Éléments déplacés');
  }, [selectedIds, currentItems, clearSelection]); // eslint-disable-line

  // Clear selection + filters when changing folder
  useEffect(() => {
    clearSelection();
    setSearchQuery('');
    setCategoryFilter(null);
  }, [currentFolderId, clearSelection]);

  // Keyboard: Delete → bulk delete, Escape → clear selection
  useEffect(() => {
    const onKey = (e) => {
      if (!selectedIds.size) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Delete') handleBulkDelete();
      if (e.key === 'Escape') clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds, handleBulkDelete, clearSelection]);

  // ---- Drag and drop ----
  // dragData: {type: 'item'|'folder', id, kind?}
  const startDrag = (data) => (e) => {
    e.dataTransfer.setData(DND_MIME, JSON.stringify(data));
    e.dataTransfer.effectAllowed = 'move';
    setDragData(data);
  };
  const endDrag = () => { setDragData(null); setDropTargetId(null); };

  // True if `data` (current drag) can land on `targetFolderId` (null = root).
  const canDropOn = useCallback((data, targetFolderId) => {
    if (!data) return false;
    if (data.type === 'folder') {
      if (data.id === targetFolderId) return false;
      if (targetFolderId && isDescendantOf(targetFolderId, data.id, folders)) return false;
      // Already in this parent: no-op
      const f = folders.find((x) => x.id === data.id);
      if (f && (f.parent_id || null) === targetFolderId) return false;
    }
    if (data.type === 'item') {
      const it = items.find((x) => x.id === data.id);
      if (it && (it.folder_id || null) === targetFolderId) return false;
    }
    return true;
  }, [folders, items]);

  const onTargetDragOver = (targetFolderId) => (e) => {
    if (!dragData) return;
    if (!canDropOn(dragData, targetFolderId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetId !== (targetFolderId ?? 'root')) setDropTargetId(targetFolderId ?? 'root');
  };
  const onTargetDragLeave = (targetFolderId) => (e) => {
    // Only clear when leaving the element itself, not crossing children.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dropTargetId === (targetFolderId ?? 'root')) setDropTargetId(null);
  };
  const onTargetDrop = (targetFolderId) => async (e) => {
    e.preventDefault();
    setDropTargetId(null);
    if (!dragData || !canDropOn(dragData, targetFolderId)) { setDragData(null); return; }
    const data = dragData;
    setDragData(null);
    try {
      if (data.type === 'item') {
        await moveLibraryItem(data.kind, data.id, targetFolderId);
        setItems((p) => p.map((x) => x.id === data.id ? { ...x, folder_id: targetFolderId } : x));
      } else if (data.type === 'folder') {
        await moveFolder(data.id, targetFolderId);
        setFolders((p) => p.map((x) => x.id === data.id ? { ...x, parent_id: targetFolderId } : x));
      }
      toast.success('Déplacé');
    } catch { toast.error('Échec du déplacement'); }
  };

  if (authLoading || !user) {
    return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  const dragSourceId = dragData?.id || null;

  return (
    <Layout title="Bibliothèque">
      <div className="h-full overflow-y-auto">
        <div className="max-w-6xl mx-auto p-8 space-y-6">

          <div>
            <h1 className="text-xl font-semibold tracking-tight">Bibliothèque visuelle</h1>
            <p className="text-sm text-muted-foreground mt-1">Glissez-déposez pour réorganiser vos personnages, décors et objets.</p>
          </div>

          {/* Creation bar */}
          <div className="flex items-center gap-2 p-4 rounded-xl border border-border bg-card flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Créer :</span>
            {Object.entries(KIND_META).map(([kind, meta]) => {
              const Icon = meta.icon;
              return (
                <Button key={kind} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
                  onClick={() => setCreatingKind(kind)} data-testid={`library-new-${kind}-btn`}>
                  <Icon className="w-3.5 h-3.5" /> {meta.singular.charAt(0).toUpperCase() + meta.singular.slice(1)}
                </Button>
              );
            })}
            <div className="w-px h-5 bg-border mx-1" />
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              data-testid="library-import-btn"
              title="Importer des images (converties en noir et blanc)"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
              Importer
            </Button>
            <input
              ref={importInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                handleImportFiles(e.target.files);
                if (importInputRef.current) importInputRef.current.value = '';
              }}
            />
          </div>

          {/* Breadcrumb + new folder.
              Each crumb is a drop target so a user mid-drag can move
              an element up the tree without first navigating there. */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <nav className="flex items-center gap-1 text-sm min-w-0 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentFolderId(null)}
                onDragOver={onTargetDragOver(null)}
                onDragLeave={onTargetDragLeave(null)}
                onDrop={onTargetDrop(null)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${
                  dropTargetId === 'root' ? 'bg-primary/15 text-primary ring-1 ring-primary/40'
                  : currentFolderId === null ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Home className="w-3.5 h-3.5" /> Bibliothèque
              </button>
              {breadcrumb.map((f) => (
                <React.Fragment key={f.id}>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <button
                    type="button"
                    onClick={() => setCurrentFolderId(f.id)}
                    onDragOver={onTargetDragOver(f.id)}
                    onDragLeave={onTargetDragLeave(f.id)}
                    onDrop={onTargetDrop(f.id)}
                    className={`px-2 py-1 rounded-md truncate transition-colors ${
                      dropTargetId === f.id ? 'bg-primary/15 text-primary ring-1 ring-primary/40'
                      : currentFolderId === f.id ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {f.name}
                  </button>
                </React.Fragment>
              ))}
            </nav>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 shrink-0" onClick={() => setShowNewFolder(true)}>
              <Plus className="w-3.5 h-3.5" /> Nouveau dossier
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-6">
              {/* Subfolders — split into owned vs admin-public so the user
                  can tell at a glance which are shared by the admin. The
                  owned section uses the same drag-drop affordances; public
                  folders are display-only for non-admins. */}
              {subfolders.length > 0 && (() => {
                const ownedFolders = subfolders.filter((f) => f.is_owner !== false);
                const publicFolders = subfolders.filter((f) => f.is_owner === false && f.is_public);
                const isAdmin = !!user?.is_admin;
                const cardProps = (f) => ({
                  folder: f,
                  thumbnails: folderThumbnails(f.id),
                  itemCount: folderItemCount(f.id),
                  isAdmin,
                  onOpen: setCurrentFolderId,
                  onRename: handleRenameFolder,
                  onDelete: handleDeleteFolder,
                  onTogglePublic: handleTogglePublic,
                  onDuplicate: handleDuplicateFolder,
                  dragSourceId,
                  isDropTarget: dropTargetId === f.id,
                  onDragStart: startDrag({ type: 'folder', id: f.id }),
                  onDragEnd: endDrag,
                  onDragOver: onTargetDragOver(f.id),
                  onDragLeave: onTargetDragLeave(f.id),
                  onDrop: onTargetDrop(f.id),
                });
                return (
                  <>
                    {ownedFolders.length > 0 && (
                      <div className="space-y-3">
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dossiers</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
                          {ownedFolders.map((f) => <FolderCard key={f.id} {...cardProps(f)} />)}
                        </div>
                      </div>
                    )}
                    {publicFolders.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Partagés</h2>
                          <Globe className="w-3 h-3 text-muted-foreground/60" />
                        </div>
                        <p className="text-xs text-muted-foreground/70">Dossiers publics partagés par l'administrateur (lecture seule).</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
                          {publicFolders.map((f) => <FolderCard key={f.id} {...cardProps(f)} />)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Éléments</h2>
                  {currentItems.length > 0 && selectedIds.size === 0 && (
                    <span className="text-[11px] text-muted-foreground/50">Ctrl+clic pour sélectionner</span>
                  )}
                  {selectedIds.size > 0 && (
                    <span className="text-[11px] text-muted-foreground">{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
                  )}
                </div>

                {/* Search + category chips */}
                {currentItems.length > 0 && (
                  <div className="flex gap-2 flex-wrap items-center">
                    <div className="relative flex-1 min-w-40">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        type="search"
                        placeholder="Rechercher par nom…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
                      />
                    </div>
                    {availableCategories.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {availableCategories.map(cat => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
                            className={`text-[11px] px-2 py-1 rounded-full border transition-all ${
                              categoryFilter === cat
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                            }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/5 text-xs">
                    <span className="flex-1 font-medium text-primary">
                      {selectedIds.size} élément{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
                    </span>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowMoveDialog(true)}>
                      <FolderOpen className="w-3 h-3" /> Déplacer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1 text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </Button>
                    <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={clearSelection} title="Désélectionner tout">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {currentItems.length === 0 ? (
                  <div className="text-center py-16 text-muted-foreground border-2 border-dashed border-border rounded-xl">
                    <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Aucun élément dans ce dossier</p>
                    <p className="text-xs mt-1 opacity-60">Utilisez les boutons de création ci-dessus ou glissez-y des éléments</p>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <p className="text-sm">Aucun résultat pour cette recherche</p>
                    <button type="button" className="text-xs text-primary mt-1 hover:underline" onClick={() => { setSearchQuery(''); setCategoryFilter(null); }}>Effacer les filtres</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredItems.map((it) => (
                      <ItemCard
                        key={it.id}
                        item={it}
                        onDelete={handleDelete}
                        onPreview={setPreviewItem}
                        onRename={handleRenameItem}
                        onSelect={toggleSelect}
                        selected={selectedIds.has(it.id)}
                        deleting={deletingId === it.id}
                        dragSourceId={dragSourceId}
                        onDragStart={startDrag({ type: 'item', id: it.id, kind: it.kind })}
                        onDragEnd={endDrag}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* ── Library Generator — admin only ── */}
          {user?.is_admin && (
            <div className="border-t border-border/50 pt-8 space-y-4">
              <div>
                <h2 className="text-base font-semibold tracking-tight flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-wider">Admin</span>
                  Library Generator
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Générez des assets en lot depuis un fichier CSV ou JSON.
                </p>
              </div>
              <LibraryGenerator onAssetsGenerated={load} />
            </div>
          )}

        </div>
      </div>

      {creatingKind && <CreateModal kind={creatingKind} folderId={currentFolderId} onCreated={handleCreated} onClose={() => setCreatingKind(null)} />}
      {showNewFolder && <NewFolderDialog onConfirm={handleCreateFolder} onClose={() => setShowNewFolder(false)} />}
      {showMoveDialog && (
        <MoveToFolderDialog
          folders={folders}
          currentFolderId={currentFolderId}
          onMove={handleBulkMove}
          onClose={() => setShowMoveDialog(false)}
        />
      )}

      <Dialog open={!!previewItem} onOpenChange={(o) => { if (!o) { setPreviewItem(null); setRefinePrompt(''); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              {previewItem && (() => { const Icon = KIND_META[previewItem.kind]?.icon || ImageIcon; return <Icon className="w-4 h-4" />; })()}
              {previewItem?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Image */}
          {previewItem && itemSrc(previewItem) && (
            <img
              key={itemSrc(previewItem)}
              src={itemSrc(previewItem)}
              alt={previewItem.name}
              className="w-full h-auto rounded-md border border-border bg-secondary/20"
            />
          )}

          {/* ── Itérer / Affiner ───────────────────────────────── */}
          {previewItem?.is_owner !== false && (
            <div className="space-y-2 pt-2 border-t border-border/50">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Affiner la génération</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Précisez la scène ou l'action pour cette itération…"
                  value={refinePrompt}
                  onChange={(e) => setRefinePrompt(e.target.value)}
                  disabled={refining || removingBg}
                  className="flex-1 h-8 text-xs"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleRefine(); } }}
                />
                <Button
                  size="sm"
                  className="h-8 gap-1.5 text-xs shrink-0"
                  onClick={async () => {
                    setRefining(true);
                    const tid = toast.loading('Génération en cours… (30-60s)');
                    try {
                      const newItem = await refineLibraryItem(previewItem.kind, previewItem.id, refinePrompt);
                      const bust = { ...newItem, canonical_url: newItem.canonical_url + '?v=' + Date.now() };
                      setPreviewItem(bust);
                      setRefinePrompt('');
                      await load(); // recharge tous les items pour garantir l'affichage
                      toast.dismiss(tid);
                      toast.success(`Nouvelle itération créée : « ${bust.name} »`);
                    } catch (err) {
                      toast.dismiss(tid);
                      toast.error(err?.response?.data?.detail || 'Échec de la génération');
                    } finally { setRefining(false); }
                  }}
                  disabled={refining || removingBg}
                >
                  {refining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Générer
                </Button>
              </div>

              {/* ── Supprimer le fond ──────────────────────────────── */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs w-full"
                onClick={async () => {
                  setRemovingBg(true);
                  const tid = toast.loading('Suppression du fond en cours…');
                  try {
                    const newItem = await removeLibraryBackground(previewItem.kind, previewItem.id);
                    const bust = { ...newItem, canonical_url: newItem.canonical_url + '?v=' + Date.now() };
                    setPreviewItem(bust);
                    await load(); // recharge tous les items pour garantir l'affichage
                    toast.dismiss(tid);
                    toast.success(`Nouvelle image créée : « ${bust.name} »`);
                  } catch (err) {
                    toast.dismiss(tid);
                    toast.error(err?.response?.data?.detail || 'Échec de la suppression du fond');
                  } finally { setRemovingBg(false); }
                }}
                disabled={refining || removingBg}
              >
                {removingBg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />}
                Supprimer le fond (Remove Background)
              </Button>
            </div>
          )}

          {/* Prompt admin */}
          {previewItem?.prompt !== undefined && (
            <div className="rounded-md border border-border bg-secondary/20 p-3 space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Prompt envoyé (admin)</div>
              {previewItem.prompt
                ? <pre className="text-xs leading-relaxed whitespace-pre-wrap break-words font-mono text-foreground/90 max-h-72 overflow-y-auto">{previewItem.prompt}</pre>
                : <p className="text-xs italic text-muted-foreground">Prompt non enregistré.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
